import type { components } from '@tallerya/api-client';
import { useTraduccion } from '@tallerya/i18n';
import { Boton } from '@tallerya/ui';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';
import { api } from '../api';
import { PuertaSinTaller } from '../sesion/Acceso';
import { talleresDelToken } from '../sesion/esDeTaller';
import { fijarTallerActivo } from './tallerActivo';

export type MiTaller = components['schemas']['MyTenant'];

// En qué taller se está trabajando (#205). Una cuenta es de una persona, y una
// persona puede estar en varios talleres: esto elige uno y lo pone a
// disposición de toda la aplicación, y de `api.ts` para la cabecera `X-Tenant-Id`.
//
// ## Qué talleres se ofrecen
//
// Los que están **a la vez** en la base (`GET /api/me/tenants`, las membresías)
// y en el token (`tenant_ids`). La base es la que manda; el token es lo que la
// API va a aceptar. Uno que está en la base y todavía no en el token —el worker
// aún no lo escribió— no se ofrece: la API le respondería 403. Aparece solo en
// la próxima renovación del token, a los pocos minutos.
//
// ## Cuál se elige
//
// El de esta pestaña, si ya eligió uno (sobrevive a recargar); si no, el último
// que usó esta persona en este navegador (decidido en #205: se entra directo al
// que casi siempre se usa); si no, el primero por nombre. Uno guardado que ya no
// está en la lista se ignora sin avisar, como la sucursal.
//
// ## La aplicación no se dibuja sin taller
//
// Mientras se averigua, se muestra «Cargando…» y nada más. Si las pantallas se
// dibujaran antes, sus consultas saldrían sin cabecera, y a quien está en varios
// talleres la API le respondería 403 a todas.

interface Contexto {
  talleres: readonly MiTaller[];
  /** Nulo solo sin sesión: con sesión, la aplicación no se dibuja sin uno. */
  actual: MiTaller | null;
  elegir: (id: string) => void;
}

const ContextoTaller = createContext<Contexto | null>(null);

/// El de esta pestaña. En `sessionStorage`: cada pestaña tiene el suyo.
const LLAVE_PESTANA = 'tallerya.admin.taller';

/// El último que usó esta persona. En `localStorage` y por persona: en la
/// computadora compartida de recepción, cada quien vuelve al suyo.
const llaveUltimo = (persona: string) => `tallerya.admin.ultimo-taller:${persona}`;

// Cualquiera de los dos almacenamientos puede no estar —navegación privada,
// almacenamiento bloqueado— y eso no puede dejar a nadie afuera: sin ellos,
// simplemente no se recuerda.
function leer(almacen: () => Storage, llave: string): string | null {
  try {
    return almacen().getItem(llave);
  } catch {
    return null;
  }
}

function guardar(almacen: () => Storage, llave: string, valor: string) {
  try {
    almacen().setItem(llave, valor);
  } catch {
    // Sin almacenamiento, la elección dura lo que dure la página.
  }
}

const sesion = () => window.sessionStorage;
const local = () => window.localStorage;

export function ProveedorTaller({ children }: { children: ReactNode }) {
  const { t } = useTraduccion();
  const auth = useAuth();
  const persona = auth.user?.profile.sub ?? 'sin-sesion';
  const delToken = talleresDelToken(auth.user?.profile).map((id) => id.toLowerCase());

  const consulta = useQuery({
    // Por persona: salir y entrar con otra en la misma pestaña no hereda la lista.
    queryKey: ['talleres', persona],
    enabled: auth.isAuthenticated,
    queryFn: async () => {
      const { data, error } = await api.GET('/api/me/tenants');
      if (error || !data) throw new Error('No se pudieron leer los talleres');
      return data.tenants;
    },
    staleTime: 5 * 60 * 1000,
  });

  const talleres = (consulta.data ?? []).filter((taller) => delToken.includes(taller.id.toLowerCase()));

  const guardado = (llave: string) => {
    const id = leer(llave === LLAVE_PESTANA ? sesion : local, llave);
    return talleres.find((taller) => taller.id === id);
  };

  const actual = auth.isAuthenticated
    ? (guardado(LLAVE_PESTANA) ?? guardado(llaveUltimo(persona)) ?? talleres[0] ?? null)
    : null;

  // El taller con el que esta página arrancó. **Nunca cambia sin recargar.** Si el
  // elegido deja de estar en la lista —una renovación del token después de que le
  // quitaron el acceso, o la lista que se vuelve a pedir—, `actual` caería en otro
  // taller y la cabecera cambiaría por debajo de una pantalla del anterior: un
  // formulario a medio llenar en López se guardaría en Morales. Por eso, en ese
  // caso, se sigue mandando el de antes —que la API rechaza, porque ya no está en
  // el token— y se recarga, como al elegir otro desde el combo.
  //
  // Es estado y no una referencia porque se lee al dibujar; fijarlo durante el
  // dibujado es la forma que React admite para recordar algo de uno anterior.
  const [conElQueArranco, setConElQueArranco] = useState<string | null>(null);
  const cambioPorDebajo = conElQueArranco !== null && actual !== null && actual.id !== conElQueArranco;

  if (actual && conElQueArranco === null) {
    setConElQueArranco(actual.id);
  }

  // **Antes de dibujar a los hijos, no en un efecto.** Las consultas de las
  // pantallas salen en sus propios efectos, que corren antes que los de este
  // componente: si la cabecera se fijara en un efecto acá, la primera tanda
  // saldría sin ella. Es escribir una variable con el mismo valor en cada
  // dibujado, así que repetirlo no cambia nada.
  fijarTallerActivo(cambioPorDebajo ? (conElQueArranco ?? undefined) : actual?.id);

  useEffect(() => {
    if (cambioPorDebajo) window.location.reload();
  }, [cambioPorDebajo]);

  // Se recuerda el que quedó, también cuando se eligió solo: así, recargar o abrir
  // otra pestaña vuelve a él aunque mañana la lista llegue en otro orden.
  useEffect(() => {
    if (!actual || cambioPorDebajo) return;
    guardar(sesion, LLAVE_PESTANA, actual.id);
    guardar(local, llaveUltimo(persona), actual.id);
  }, [actual, persona, cambioPorDebajo]);

  // Sin sesión la aplicación se dibuja igual —la barra ofrece «Entrar»—, y no hay
  // taller que elegir.
  if (!auth.isAuthenticated) {
    return <ContextoTaller.Provider value={{ talleres: [], actual: null, elegir: () => {} }}>{children}</ContextoTaller.Provider>;
  }

  // Mientras se averigua, y mientras se recarga por un cambio por debajo: nada de
  // la aplicación a la vista.
  if (consulta.isPending || cambioPorDebajo) {
    return (
      <main className="puerta">
        <p className="puerta__marca">{t('app.marca')}</p>
        <p>{t('app.cargando')}</p>
      </main>
    );
  }

  // Solo si no hay lista. Un fallo al volver a pedirla —un corte de red y su
  // reconexión— deja la de antes en `data`, y desmontar la aplicación por eso se
  // llevaría un formulario a medio llenar.
  if (consulta.isError && !consulta.data) {
    return (
      <main className="puerta">
        <p className="puerta__marca">{t('app.marca')}</p>
        <h1>{t('admin.taller.error.titulo')}</h1>
        <p>{t('admin.taller.error.texto')}</p>
        <Boton onClick={() => void consulta.refetch()}>{t('app.reintentar')}</Boton>
      </main>
    );
  }

  // El token decía que sí y la base dice que no: a la persona la sacaron de sus
  // talleres y el token todavía no vence. Gana la negación, como en la API.
  if (!actual) {
    return <PuertaSinTaller />;
  }

  const valor: Contexto = {
    talleres,
    actual,
    elegir: (id) => {
      if (id === actual.id) return;

      guardar(sesion, LLAVE_PESTANA, id);
      guardar(local, llaveUltimo(persona), id);

      // **Se recarga la página entera, y en el inicio.** Todo lo que hay en
      // memoria es del taller anterior: la caché, la sucursal elegida, el
      // formulario a medio llenar, la ficha abierta —cuyo id no existe en el otro
      // taller—. Recargar es la única forma de no tener que acordarse de vaciar
      // cada cosa, y la sesión de Keycloak sigue abierta: no pide contraseña.
      window.location.assign('/');
    },
  };

  return <ContextoTaller.Provider value={valor}>{children}</ContextoTaller.Provider>;
}

export function useTaller(): Contexto {
  const contexto = useContext(ContextoTaller);
  if (!contexto) {
    throw new Error('useTaller se usa dentro de ProveedorTaller');
  }
  return contexto;
}

/**
 * El taller de la pestaña, para las claves de caché: regla 5 de multitenancy, la
 * versión en el navegador. Hasta #205 salía del `tenant_slug` del token; ahora el
 * token trae varios y el que cuenta es el elegido.
 */
export function useTallerDeLaCache(): string {
  return useTaller().actual?.id ?? 'sin-sesion';
}
